import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { useBreadcrumbs } from '@dxaas/frontend-core'
import { Card, CardContent, resolveLucideIcon } from '@dxaas/ui'
import { ArrowRight, HelpCircle, Info, LayoutGrid } from 'lucide-react'

import { useDialogosDeAplicacion } from '@/components/common/DialogosDeAplicacion'
import { useAuth } from '@/hooks/useAuth'
import { esAplicacionAdministrativa } from '@/utils/aplicacionesAdministrativas'

// TDD-035 — home page "Workspace" (inspirada en el Workspace de Lotus
// Notes/Domino): lista las aplicaciones de negocio a las que el usuario
// tiene acceso (user.aplicacionesCatalogo, mismo dato ya usado por el
// selector de aplicaciones del Sidebar), reemplazando moduleRegistry
// (código muerto desde TDD-010 v2, sin ninguna aplicación real detrás).
// Las administrativas (TNADM/PLADM) se excluyen — se acceden vía su propio
// ícono del Header, la home es un workspace solo de aplicaciones de negocio.
const HomePage: React.FC = () => {
  const { user } = useAuth()
  const { setBreadcrumbs } = useBreadcrumbs()
  const navigate = useNavigate()
  // TDD-024 v1.9 — About y Ayuda viven en DialogosDeAplicacionProvider, el
  // mismo dueño que usan el Header y las vistas remotas.
  const { abrirAcercaDe, abrirAyuda } = useDialogosDeAplicacion()

  // TDD-051 — Breadcrumbs.tsx ya sintetiza el ítem "Workspace" (index 0) en
  // todo breadcrumb no vacío; la home page es exactamente esa página, así
  // que no necesita agregar un ítem propio (evita "Workspace > Inicio"
  // duplicado y no clickeable).
  useEffect(() => {
    setBreadcrumbs([])
  }, [setBreadcrumbs])

  const aplicaciones = (user?.aplicacionesCatalogo ?? []).filter(
    (aplicacion) => !esAplicacionAdministrativa(aplicacion.applicationId)
  )

  return (
    <div className="mx-auto max-w-5xl px-6 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          Bienvenido{user?.nombre ? `, ${user.nombre}` : ''}
        </h1>
        <p className="mt-2 text-gray-500">Seleccioná una aplicación para comenzar</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {aplicaciones.map((aplicacion) => {
          const Icon = resolveLucideIcon(aplicacion.icono) ?? LayoutGrid
          return (
            <Card key={aplicacion.applicationId} className="transition-shadow hover:shadow-md">
              <CardContent className="flex flex-col gap-4 p-6">
                <div
                  className="flex cursor-pointer items-center justify-between"
                  onClick={() => navigate(`/${aplicacion.applicationId.toLowerCase()}`)}
                >
                  <div className="flex items-center gap-4">
                    <div className="rounded-lg bg-blue-50 p-3 text-blue-600">
                      <Icon className="h-6 w-6" />
                    </div>
                    <span className="text-lg font-semibold text-gray-900">{aplicacion.nombre}</span>
                  </div>
                  <ArrowRight className="h-5 w-5 text-gray-400" />
                </div>

                <div className="flex items-center gap-2 border-t border-gray-100 pt-3">
                  <button
                    onClick={() => abrirAcercaDe(aplicacion)}
                    className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                  >
                    <Info className="h-3.5 w-3.5" />
                    Acerca de
                  </button>
                  {aplicacion.ayudaDisponible && (
                    <button
                      onClick={() => abrirAyuda(aplicacion)}
                      className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                    >
                      <HelpCircle className="h-3.5 w-3.5" />
                      Ayuda
                    </button>
                  )}
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>
    </div>
  )
}

export default HomePage
